import {
  ChipsOption,
  DataStatus,
  FormControl,
  FormField,
  Label,
  Separator,
  Switch,
  Tooltip,
} from 'platform/components';
import {
  Box,
  Clickable,
  Grid,
  GridItem,
  Hide,
  HStack,
  Icon,
  Link,
  Show,
  Text,
  useDevice,
  VStack,
} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil, sort} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {ApiError, getSafely, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import {useFilter} from '../../hooks/FilterContext';
import {useCatalogue} from '../../hooks/useCatalogue';
import i18n from '../../i18n/i18n';
import {useGetApiCountriesQuery, useGetTrimLevelsQuery} from '../../store/catalogueApi';
import {useLazyGetAdvertisementServerLinkQuery} from '../../store/priceReportApi';
import {FilterFormData} from '../../types/FilterFormData';
import {PriceReportType} from '../../types/PriceReportType';
import {featureFlags} from '../../utils/featureFlags';
import {getCurrencyCodeFromString} from '../../utils/getCurrencyCodeFromString';
import {getDaysInAdvertisementOptions} from '../../utils/getDaysInAdvertisementOptions';
import {getFeatureScoreOptions} from '../../utils/getFeatureScoreOptions';
import {getPowerOptions} from '../../utils/getPowerOptions';
import {getPriceOptions} from '../../utils/getPriceOptions';
import {getYearOptions} from '../../utils/getYearOptions';
import {handleApiError} from '../../utils/handleApiError';
import {isUnsupportedCombination} from '../../utils/isUnsupportedCombination';
import {getMileageOptions} from '../../utils/mileageOptions';
import {QueryStateKeys} from '../../utils/routing';
import {AdServerLogo} from '../AdServerLogo/AdServerLogo';
import {AddressSearch} from './components/AddressSearch';
import {Search} from './components/Search';
import {getOptionsWithPriority} from './utils/getOptionsWithPriority';

const TRANSMISSION_OPTIONS_SHOWN = 2;
const FUEL_OPTIONS_SHOWN = 2;

export interface FilterProps extends TestIdProps {
  priceReport: PriceReportType;
  formControl: FormControl<FilterFormData>;
  formApi: UseFormReturn<FilterFormData>;
  onReset?: () => void;
}

export function Filter(props: FilterProps) {
  const {driveOptions, fuelOptions, styleOptions, transmissionOptions, sellerTypeOptions} =
    useCatalogue();
  const {filter, errors} = useFilter();
  const [debug, setDebug] = useQueryState(QueryStateKeys.Debug);
  const device = useDevice();
  const [getLink, {isFetching}] = useLazyGetAdvertisementServerLinkQuery();
  const driveOptionsWithPriority = getOptionsWithPriority(driveOptions, filter?.drive);
  const chipsSize = device === 'mobile' || device === 'tablet' ? 'default' : 'small';
  const isFromCebia =
    props.priceReport.serviceCode === 'cebia' || props.priceReport.serviceCode === 'cebia-dev';

  const showStockCars = props.formApi.watch('showStockCars');
  const showSoldIn30 = props.formApi.watch('showSoldIn30');
  const showSoldIn90 = props.formApi.watch('showSoldIn90');
  const priceGte = props.formApi.watch('priceGte');
  const priceLte = props.formApi.watch('priceLte');
  const firstRegistrationOnYearGte = props.formApi.watch('firstRegistrationOnYearGte');
  const firstRegistrationOnYearLte = props.formApi.watch('firstRegistrationOnYearLte');
  const mileageGte = props.formApi.watch('mileageGte');
  const mileageLte = props.formApi.watch('mileageLte');
  const daysInAdvertisementGte = props.formApi.watch('daysInAdvertisementGte');
  const daysInAdvertisementLte = props.formApi.watch('daysInAdvertisementLte');
  const powerGte = props.formApi.watch('powerGte');
  const powerLte = props.formApi.watch('powerLte');
  const featureScoreGte = props.formApi.watch('featureScoreGte');
  const featureScoreLte = props.formApi.watch('featureScoreLte');

  const isShowStockCarsDisabled = isUnsupportedCombination(
    !showStockCars,
    showSoldIn30,
    showSoldIn90
  );
  const isShowSoldIn30Disabled = isUnsupportedCombination(
    showStockCars,
    !showSoldIn30,
    showSoldIn90
  );
  const isShowSoldIn90Disabled = isUnsupportedCombination(
    showStockCars,
    showSoldIn30,
    !showSoldIn90
  );

  const yesNoOptions: ChipsOption[] = [
    {label: i18n.t('general.labels.yes'), value: 'YES'},
    {label: i18n.t('general.labels.no'), value: 'NO'},
  ];

  const vehicleTransmissionOptionVisible =
    (transmissionOptions?.findLastIndex((option) => filter?.transmission?.includes(option.value)) ??
      0) < TRANSMISSION_OPTIONS_SHOWN;
  const vehicleFuelOptionVisible =
    (fuelOptions?.findLastIndex((option) => filter?.fuelType?.includes(option.value)) ?? 0) <
    FUEL_OPTIONS_SHOWN;
  const sellerTypeOptionsVisible =
    sellerTypeOptions?.filter((option) =>
      ['SELLERTYPE_PRIVATE_SELLER', 'SELLERTYPE_DEALERSHIP'].includes(option.value)
    ) ?? [];

  const {data: apiCountries, isLoading: isApiCountriesLoading} = useGetApiCountriesQuery({
    format: 'iso3',
  });
  const {data: trimLevels, isLoading: isTrimLevelsLoading} = useGetTrimLevelsQuery({
    modelFamily: props.priceReport.vehicle.modelFamily,
    make: props.priceReport.vehicle.make,
    country: getSafely(apiCountries, props.priceReport.market),
    yearFrom: firstRegistrationOnYearGte ?? undefined,
    yearTo: firstRegistrationOnYearLte ?? undefined,
  });
  const hasTrimLevels =
    !isApiCountriesLoading && !isTrimLevelsLoading && isNotNilOrEmpty(trimLevels?.trim_levels);
  const trimLevelsOptions = sort(
    (a, b) => {
      if (isNotNil(a.label) && isNotNil(b.label)) {
        return a.label.localeCompare(b.label);
      }
      return 0;
    },
    trimLevels?.trim_levels?.map((trimLevel) => ({
      label: trimLevel.label,
      value: trimLevel.const_key,
    })) ?? []
  );

  const getAdvertisementServerLink = () => {
    getLink({priceReportId: props.priceReport.id, filter})
      .then((response) => {
        if (isNil(response.data?.url)) {
          throw new Error("Server hasn't returned the URL!");
        }
        window.open(response.data?.url, '_blank');
      })
      .catch((error: ApiError) => {
        handleApiError(error);
      });
  };

  return (
    <VStack spacing={4}>
      <Hide when={isFromCebia}>
        <VStack>
          <Label tooltip={i18n.t('entity.filterForm.advertisementServerTooltip')}>
            {i18n.t('entity.filterForm.advertisementServer')}
          </Label>
          <Clickable onClick={getAdvertisementServerLink}>
            <Box
              paddingVertical={4}
              paddingHorizontal={2}
              backgroundColor="palettes.neutral.20.100"
              borderRadius="small"
            >
              <HStack justify="space-between" align="center">
                <AdServerLogo market={props.priceReport.market} maxHeight={5} />
                <DataStatus spinnerSize="small" isLoading={isFetching}>
                  <Icon value="action/open_in_new" size={4} color="palettes.neutral.800.100" />
                </DataStatus>
              </HStack>
            </Box>
          </Clickable>
        </VStack>
      </Hide>

      <VStack>
        <Label>{i18n.t('entity.filterForm.labels.title')}</Label>
        <Search
          name="title"
          control={props.formControl}
          placeholder={i18n.t('entity.filterForm.placeholders.title')}
        />
      </VStack>
      <Separator spacing={1} />

      <AddressSearch
        name="address"
        control={props.formControl}
        data-testid={suffixTestId('address', props)}
      />
      <FormField
        control={props.formControl}
        name="radius"
        type="numberInputWithOptions"
        label={`${i18n.t('entity.filterForm.radius')} (km)`}
        placeholder={i18n.t('entity.filterForm.placeholders.radius')}
        options={[5, 10, 20, 50, 100]}
        errorMessage={errors?.radius}
        data-testid={suffixTestId('radius', props)}
        isClearable
      />
      <Separator spacing={1} />

      <Tooltip label={getTooltipLabel(isShowStockCarsDisabled)} placement="top-start">
        <FormField
          control={props.formControl}
          name="showStockCars"
          type="switch"
          label={i18n.t('entity.filterForm.labels.showStockCars')}
          isDisabled={isShowStockCarsDisabled}
          data-testid={suffixTestId('showStockCars', props)}
        />
      </Tooltip>
      <Tooltip label={getTooltipLabel(isShowSoldIn30Disabled)} placement="top-start">
        <FormField
          control={props.formControl}
          name="showSoldIn30"
          type="switch"
          label={i18n.t('entity.filterForm.labels.showSoldIn30')}
          isDisabled={isShowSoldIn30Disabled}
          data-testid={suffixTestId('showSoldIn30', props)}
        />
      </Tooltip>
      <Tooltip label={getTooltipLabel(isShowSoldIn90Disabled)} placement="top-start">
        <FormField
          control={props.formControl}
          name="showSoldIn90"
          type="switch"
          label={i18n.t('entity.filterForm.labels.showSoldIn90')}
          isDisabled={isShowSoldIn90Disabled}
          data-testid={suffixTestId('showSoldIn90', props)}
        />
      </Tooltip>
      <HStack align="center" spacing={1}>
        <Switch
          value={debug === 'true'}
          onChange={(value) => setDebug(value ? 'true' : 'false')}
          label={i18n.t('entity.filterForm.labels.showDebugCars')}
          data-testid={suffixTestId('showDebugCars', props)}
        />
        <Tooltip description={i18n.t('entity.filterForm.labels.debugCarsTooltip')}>
          <Icon value="action/help" color="text.secondary" size={4} />
        </Tooltip>
      </HStack>

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="priceGte"
          type="numberInputWithOptions"
          label={`${i18n.t('entity.filterForm.price')} (${
            props.priceReport.sellingPrice.recommended.currency ?? ''
          })`}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getPriceOptions(
            getCurrencyCodeFromString(props.priceReport.sellingPrice.recommended.currency),
            null,
            priceLte
          )}
          data-testid={suffixTestId('priceGte', props)}
          isInvalid={!!errors?.price}
          isClearable
        />

        <FormField
          control={props.formControl}
          name="priceLte"
          type="numberInputWithOptions"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getPriceOptions(
            getCurrencyCodeFromString(props.priceReport.sellingPrice.recommended.currency),
            priceGte,
            null
          )}
          data-testid={suffixTestId('priceLte', props)}
          isInvalid={!!errors?.price}
          isClearable
        />

        <Show when={!!errors?.price}>
          <GridItem span={2}>
            <Text size="small" color="danger" data-testid={suffixTestId('priceError', props)}>
              {errors?.price}
            </Text>
          </GridItem>
        </Show>
      </Grid>

      <FormField
        control={props.formControl}
        name="isVatDeductible"
        label={i18n.t('entity.filterForm.VATDeductible')}
        type="chips"
        size={chipsSize}
        options={yesNoOptions}
        isMultiple
        data-testid={suffixTestId('isVatDeductible', props)}
      />

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="firstRegistrationOnYearGte"
          type="choice"
          label={i18n.t('entity.filterForm.registration')}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getYearOptions(null, firstRegistrationOnYearLte)}
          data-testid={suffixTestId('yearGte', props)}
        />

        <FormField
          control={props.formControl}
          name="firstRegistrationOnYearLte"
          type="choice"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getYearOptions(firstRegistrationOnYearGte)}
          data-testid={suffixTestId('yearLte', props)}
        />
      </Grid>

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="mileageGte"
          type="numberInputWithOptions"
          label={`${i18n.t('entity.filterForm.millage')} (km)`}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getMileageOptions(null, mileageLte)}
          data-testid={suffixTestId('mileageGte', props)}
          isInvalid={!!errors?.mileage}
          isClearable
        />

        <FormField
          control={props.formControl}
          name="mileageLte"
          type="numberInputWithOptions"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getMileageOptions(mileageGte)}
          data-testid={suffixTestId('mileageLte', props)}
          isInvalid={!!errors?.mileage}
          isClearable
        />

        <Show when={!!errors?.mileage}>
          <GridItem span={2}>
            <Text size="small" color="danger" data-testid={suffixTestId('mileageError', props)}>
              {errors?.mileage}
            </Text>
          </GridItem>
        </Show>
      </Grid>

      <FormField
        control={props.formControl}
        name="transmission"
        label={i18n.t('entity.filterForm.transmission')}
        type="chips"
        size={chipsSize}
        limit={vehicleTransmissionOptionVisible ? TRANSMISSION_OPTIONS_SHOWN : undefined}
        options={(transmissionOptions ?? []) as ChipsOption[]}
        isMultiple
        data-testid={suffixTestId('transmission', props)}
      />

      <FormField
        label={i18n.t('entity.filterForm.fuel')}
        control={props.formControl}
        name="fuelType"
        type="chips"
        size={chipsSize}
        limit={vehicleFuelOptionVisible ? FUEL_OPTIONS_SHOWN : undefined}
        options={(fuelOptions ?? []) as ChipsOption[]}
        isMultiple
        data-testid={suffixTestId('fuelType', props)}
      />

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="powerGte"
          type="numberInputWithOptions"
          label={`${i18n.t('entity.filterForm.power')} (kW)`}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getPowerOptions(null, powerLte)}
          data-testid={suffixTestId('powerGte', props)}
          isInvalid={!!errors?.power}
          isClearable
        />

        <FormField
          control={props.formControl}
          name="powerLte"
          type="numberInputWithOptions"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getPowerOptions(powerGte)}
          data-testid={suffixTestId('powerLte', props)}
          isInvalid={!!errors?.power}
          isClearable
        />

        <Show when={!!errors?.power}>
          <GridItem span={2}>
            <Text size="small" color="danger">
              {errors?.power}
            </Text>
          </GridItem>
        </Show>
      </Grid>

      {/* menuInPortal attr in FormField is needed, because otherwise the menu
          is inside the StickyContainer, whose position is sticky when
          scrolling up, and relative components can cover it */}
      <FormField
        control={props.formControl}
        name="bodyStyle"
        type="multiChoice"
        label={i18n.t('entity.filterForm.body')}
        options={styleOptions ?? []}
        menuInPortal
        data-testid={suffixTestId('bodyStyle', props)}
      />

      <FormField
        control={props.formControl}
        name="drive"
        type="chips"
        size={chipsSize}
        label={i18n.t('entity.filterForm.propulsion')}
        limit={driveOptionsWithPriority.length > 2 ? 2 : undefined}
        options={driveOptions ?? []}
        isMultiple
        data-testid={suffixTestId('drive', props)}
      />

      <FormField
        control={props.formControl}
        name="trimLevels"
        type="multiChoice"
        label={i18n.t('entity.filterForm.featureTiers')}
        options={trimLevelsOptions ?? []}
        menuInPortal
        menuPlacement="top"
        isDisabled={!hasTrimLevels}
        data-testid={suffixTestId('trimLevels', props)}
      />

      <Grid columns={2} spacing={2} align="flex-end">
        <FormField
          control={props.formControl}
          name="featureScoreGte"
          type="numberInputWithOptions"
          label={`${i18n.t('entity.filterForm.featureScore')} (%)`}
          placeholder={i18n.t('entity.filterForm.from')}
          options={getFeatureScoreOptions(null, featureScoreLte)}
          data-testid={suffixTestId('featureScoreGte', props)}
          isInvalid={!!errors?.featureScore}
          isClearable
        />

        <FormField
          control={props.formControl}
          name="featureScoreLte"
          type="numberInputWithOptions"
          placeholder={i18n.t('entity.filterForm.to')}
          options={getFeatureScoreOptions(featureScoreGte)}
          data-testid={suffixTestId('featureScoreLte', props)}
          isInvalid={!!errors?.featureScore}
          isClearable
        />

        <Show when={!!errors?.featureScore}>
          <GridItem span={2}>
            <Text size="small" color="danger">
              {errors?.featureScore}
            </Text>
          </GridItem>
        </Show>
      </Grid>

      <Show whenFeatureEnabled={featureFlags.PR_FILTERS_DAYS_ON_STOCK}>
        <Grid columns={2} spacing={2} align="flex-end">
          <FormField
            control={props.formControl}
            name="daysInAdvertisementGte"
            type="numberInputWithOptions"
            label={i18n.t('entity.filterForm.daysInAdvertisement')}
            placeholder={i18n.t('entity.filterForm.from')}
            options={getDaysInAdvertisementOptions(null, daysInAdvertisementLte)}
            data-testid={suffixTestId('daysInAdvertisementGte', props)}
            isInvalid={!!errors?.daysInAdvertisement}
            isClearable
          />

          <FormField
            control={props.formControl}
            name="daysInAdvertisementLte"
            type="numberInputWithOptions"
            placeholder={i18n.t('entity.filterForm.to')}
            options={getDaysInAdvertisementOptions(daysInAdvertisementGte)}
            data-testid={suffixTestId('daysInAdvertisementLte', props)}
            isInvalid={!!errors?.daysInAdvertisement}
            isClearable
          />

          <Show when={!!errors?.daysInAdvertisement}>
            <GridItem span={2}>
              <Text
                size="small"
                color="danger"
                data-testid={suffixTestId('daysInAdvertisementError', props)}
              >
                {errors?.daysInAdvertisement}
              </Text>
            </GridItem>
          </Show>
        </Grid>
      </Show>

      <Show whenFeatureEnabled={featureFlags.PR_FILTERS_SELLER_TYPE}>
        <FormField
          control={props.formControl}
          name="sellerType"
          label={i18n.t('entity.table.sellerType')}
          type="chips"
          size={chipsSize}
          options={sellerTypeOptionsVisible}
          isMultiple
          data-testid={suffixTestId('sellerType', props)}
        />
      </Show>

      <Show when={isNotNil(props.onReset)}>
        <Separator spacing={1} />
        <Link
          size="small"
          leftIcon="navigation/refresh"
          onClick={props.onReset}
          title={i18n.t('entity.filterForm.restoreOriginalSettings')}
          data-testid={suffixTestId('reset', props)}
        />
      </Show>
    </VStack>
  );
}

const getTooltipLabel = (isDisabled: boolean) =>
  isDisabled ? i18n.t('entity.filterForm.labels.unsupportedCombination') : undefined;
